/**
 * 🚀 Performance Plugin para Vue 3
 * DÍA 4 PASO 4.3: Integración con el ecosistema Vue
 */

import { performanceManager } from '@/services/performance'
import { ref, computed, onMounted, onUnmounted } from 'vue'

// Plugin de Vue para inyección global
export const PerformancePlugin = {
  install(app, options = {}) {
    // Configurar el performance manager
    performanceManager.configure(options)

    // Inyectar globalmente
    app.config.globalProperties.$performance = performanceManager
    app.provide('performance', performanceManager)

    // Añadir directiva v-preload
    app.directive('preload', {
      mounted(el, binding) {
        const endpoints = Array.isArray(binding.value) ? binding.value : [binding.value]
        performanceManager.preloadCriticalData(endpoints)
      }
    })

    // Interceptor automático para Axios si está disponible
    if (app.config.globalProperties.$http) {
      setupAxiosInterceptor(app.config.globalProperties.$http)
    }
  }
}

// Composable para usar en componentes
export function usePerformance() {
  const stats = ref(performanceManager.getStats())
  const isOptimizing = ref(false)
  
  // Actualizar stats cada 5 segundos
  let statsInterval = null

  const startStatsMonitoring = () => {
    if (statsInterval) return
    
    statsInterval = setInterval(() => {
      stats.value = performanceManager.getStats()
    }, 5000)
  }

  const stopStatsMonitoring = () => {
    if (statsInterval) {
      clearInterval(statsInterval)
      statsInterval = null
    }
  }

  // Métricas computadas
  const cacheHealth = computed(() => {
    const hitRate = parseFloat(stats.value.cacheHitRate)
    if (hitRate >= 80) return { status: 'excellent', color: 'success' }
    if (hitRate >= 60) return { status: 'good', color: 'primary' }
    if (hitRate >= 40) return { status: 'fair', color: 'warning' }
    return { status: 'poor', color: 'error' }
  })

  const performanceScore = computed(() => {
    const hitRate = parseFloat(stats.value.cacheHitRate)
    const avgResponse = stats.value.avgResponseTime
    const errorRate = stats.value.errorRate * 100

    let score = 100
    score -= (100 - hitRate) * 0.3 // 30% del score por hit rate
    score -= Math.min(avgResponse / 10, 50) // Penalizar respuestas lentas
    score -= errorRate * 2 // Penalizar errores

    return Math.max(0, Math.round(score))
  })

  // Funciones de utilidad
  const optimizedRequest = async (url, options = {}) => {
    isOptimizing.value = true
    try {
      return await performanceManager.optimizedRequest(url, options)
    } finally {
      isOptimizing.value = false
    }
  }

  const preloadData = async (endpoints) => {
    return performanceManager.preloadCriticalData(endpoints)
  }

  const batchRequests = async (requests) => {
    isOptimizing.value = true
    try {
      return await performanceManager.batchRequests(requests)
    } finally {
      isOptimizing.value = false
    }
  }

  const clearCache = (pattern) => {
    performanceManager.clearCache(pattern)
    stats.value = performanceManager.getStats()
  }

  const exportMetrics = () => {
    return performanceManager.exportMetrics()
  }

  onMounted(() => {
    startStatsMonitoring()
  })

  onUnmounted(() => {
    stopStatsMonitoring()
  })

  return {
    // Estado
    stats,
    isOptimizing,
    cacheHealth,
    performanceScore,

    // Métodos
    optimizedRequest,
    preloadData,
    batchRequests,
    clearCache,
    exportMetrics,
    startStatsMonitoring,
    stopStatsMonitoring
  }
}

// Composable específico para APIs de MGI
export function useMGIOptimizedAPI() {
  const { optimizedRequest, batchRequests, preloadData } = usePerformance()

  // Endpoints críticos para preload
  const criticalEndpoints = [
    { url: '/api/dashboard/summary', options: { ttl: 5 * 60 * 1000 } }, // 5 min
    { url: '/api/users/profile', options: { ttl: 10 * 60 * 1000 } }, // 10 min
    { url: '/api/analytics/kpis', options: { ttl: 5 * 60 * 1000 } } // 5 min
  ]

  // Precargar datos críticos al inicializar
  const preloadCriticalData = () => {
    return preloadData(criticalEndpoints)
  }

  // P13-V1D (C10): el request-leg de incidencias fue RETIRADO (composable sin consumidores;
  // el listado canónico es server-scoped vía incidentsService).

  // Request optimizado para dashboard
  const getDashboardData = async (role) => {
    return optimizedRequest(`/api/dashboard/${role}`, { ttl: 5 * 60 * 1000 })
  }

  // Request optimizado para analytics
  const getAnalytics = async (timeRange = '24h') => {
    return optimizedRequest(`/api/analytics/summary?range=${timeRange}`, { 
      ttl: 10 * 60 * 1000 // Cache por 10 minutos para analytics
    })
  }

  // Batch request para datos del dashboard completo
  const loadDashboardBatch = async (role) => {
    const requests = [
      { url: `/api/dashboard/${role}`, options: {} },
      { url: '/api/analytics/kpis', options: {} },
      { url: '/api/users/notifications', options: {} }
    ]

    return batchRequests(requests)
  }

  return {
    preloadCriticalData,
    getDashboardData,
    getAnalytics,
    loadDashboardBatch
  }
}

// Setup del interceptor de Axios (si está disponible)
function setupAxiosInterceptor(axios) {
  // Request interceptor
  axios.interceptors.request.use(
    (config) => {
      // Marcar el inicio del request
      config.metadata = { startTime: Date.now() }
      return config
    },
    (error) => Promise.reject(error)
  )

  // Response interceptor
  axios.interceptors.response.use(
    (response) => {
      // Calcular tiempo de respuesta
      const endTime = Date.now()
      const duration = endTime - response.config.metadata.startTime
      
      // Actualizar métricas
      performanceManager.updatePerformanceMetrics(duration, true)
      
      return response
    },
    (error) => {
      // Manejar errores
      if (error.config && error.config.metadata) {
        const duration = Date.now() - error.config.metadata.startTime
        performanceManager.updatePerformanceMetrics(duration, false)
      }
      
      return Promise.reject(error)
    }
  )
}

// Utilidades para desarrollo
export class PerformanceDevTools {
  static logCacheStats() {
    const stats = performanceManager.getStats()
    console.group('📊 Performance Stats')
    console.log('Cache Hit Rate:', `${stats.cacheHitRate}%`)
    console.log('Cache Size:', stats.cacheSize)
    console.log('Memory Usage:', `${stats.memoryUsage}KB`)
    console.log('Avg Response Time:', `${stats.avgResponseTime.toFixed(2)}ms`)
    console.log('Error Rate:', `${(stats.errorRate * 100).toFixed(2)}%`)
    console.groupEnd()
  }

  static exportMetricsToFile() {
    const metrics = performanceManager.exportMetrics()
    const blob = new Blob([JSON.stringify(metrics, null, 2)], { 
      type: 'application/json' 
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `mgi-performance-metrics-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  static simulateLoad(requests = 100, delay = 100) {
    console.log(`🧪 Simulando ${requests} requests con ${delay}ms de delay...`)
    
    const testEndpoints = [
      '/api/test/endpoint1',
      '/api/test/endpoint2',
      '/api/test/endpoint3'
    ]

    for (let i = 0; i < requests; i++) {
      setTimeout(() => {
        const endpoint = testEndpoints[i % testEndpoints.length]
        performanceManager.optimizedRequest(`${endpoint}?test=${i}`)
          .catch(() => {}) // Ignorar errores en el test
      }, i * delay)
    }
  }
}

// Exportar para uso en desarrollo
if (process.env.NODE_ENV === 'development') {
  window.MGIPerformanceDevTools = PerformanceDevTools
}
